import React from 'react';
import { motion } from 'framer-motion';
import { Award, Building2, Package, Truck, ShoppingBag } from 'lucide-react';
import { COMPLIANCE_BADGES, INDUSTRY_PRESENCE } from '../config/siteContent';

const ICONS = { Building2, Package, Truck, ShoppingBag };

/**
 * CustomersSection - light theme.
 *
 * Previous version showed customer LOGOS in an infinite marquee - that was
 * removed in May 2026 because (a) we don't have explicit logo licensing from
 * the brands, and (b) the marquee duplicated logos in close proximity which
 * looked broken. Replaced with industry-presence cards that communicate the
 * same "we work with the biggest names" story without using their marks.
 *
 * Compliance strip below uses the audited COMPLIANCE_BADGES list - only EDI
 * standards iConnect demonstrably handles, no over-claimed regulatory marks.
 */
const CustomersSection = () => {
  return (
    <section
      id="customers"
      className="relative py-24 lg:py-32 bg-bg-canvas overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0.6, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14 max-w-3xl xl:max-w-none mx-auto"
        >
          <p className="eyebrow">Trusted Worldwide</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-text-primary mb-5 tracking-[-0.02em] text-balance xl:whitespace-nowrap">
            <span className="text-gradient-sky">Where</span>{' '}
            the world's manufacturers connect
          </h2>
          <p className="text-lg text-text-secondary text-pretty">
            From automotive OEMs and their suppliers at every tier to major
            industrial manufacturers and retailers - iConnect powers the EDI
            behind some of the most demanding supply chains.
          </p>
        </motion.div>

        {/* Industry-presence cards (replaces logo marquee) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {INDUSTRY_PRESENCE.map((industry, idx) => {
            const Icon = ICONS[industry.icon] || Building2;
            return (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0.5, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.45, delay: idx * 0.05 }}
                className="card-elevated p-6 lg:p-7"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-gradient-sky shadow-glow-sky flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-white" strokeWidth={2.2} />
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display font-extrabold text-2xl text-gradient-sky leading-none tracking-tight">
                      {industry.partnerCount}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
                      partners
                    </span>
                  </div>
                </div>
                <h3 className="font-display font-bold text-lg text-text-primary mb-2 tracking-tight">
                  {industry.name}
                </h3>
                <p className="text-sm text-text-secondary leading-relaxed">
                  {industry.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Compliance strip - only EDI standards iConnect actually handles. */}
        <motion.div
          initial={{ opacity: 0.5, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.5 }}
          className="card-elevated p-8 lg:p-10 text-center"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <Award size={18} className="text-accent-primary" />
            <span className="eyebrow !mb-0">EDI Standards & Protocols</span>
          </div>
          <div className="flex flex-wrap justify-center gap-2.5">
            {COMPLIANCE_BADGES.map((badge) => (
              <span key={badge} className="badge-sky">
                {badge}
              </span>
            ))}
          </div>
          <p className="text-xs text-text-muted mt-4 max-w-xl mx-auto">
            Technical standards iConnect's VAN routes, translates, and supports
            day-to-day.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default CustomersSection;
